import { Component, type ErrorInfo, type ReactNode } from "react";
import { TrackJS } from "trackjs";

type Props = { children: ReactNode };
type State = { hasError: boolean };

/**
 * React Error Boundary that forwards uncaught render errors to TrackJS.
 * Wraps the entire app so that component-tree errors are captured with
 * their component stack trace for debugging in the TrackJS dashboard.
 */
export class TrackJSErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (TrackJS.isInstalled()) {
      if (errorInfo.componentStack) {
        TrackJS.console.log(errorInfo.componentStack);
      }
      TrackJS.track(error);
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100dvh",
            padding: "1rem",
            textAlign: "center",
          }}
        >
          <p style={{ marginBottom: "1rem", fontSize: "1.125rem" }}>
            Something went wrong. Please try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1.5rem",
              borderRadius: "0.5rem",
              border: "none",
              background: "#333",
              color: "#fff",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
